import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { Indicadores } from "@/components/Indicadores";
import { getUsage, type UsageRow } from "@/api/log.service";
import { getClients, type Client } from "@/api/client.service";
import { getAllModules, type ManifestModule } from "@/api/modules.service";
import { arvore, resumo, type Dimensao, type No } from "./usoApp";

type Visao = "usuario" | "modulo";

const formatarDia = (dia: string) => dayjs(dia).format("DD/MM");
const formatarUltimo = (ts: string) => (ts ? dayjs(ts).format("DD/MM/YYYY HH:mm") : "—");

function Dias({ dias }: { dias: string[] }) {
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5}>
      {dias.map((d) => (
        <Chip key={d} label={formatarDia(d)} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

// Linha de primeiro nível (usuário ou módulo) que abre a lista do outro eixo.
function LinhaExpansivel({ no, rotuloFilho }: { no: No; rotuloFilho: string }) {
  const [aberta, setAberta] = useState(false);
  return (
    <Fragment>
      <TableRow hover onClick={() => setAberta((v) => !v)} sx={{ cursor: "pointer", "& > td": { borderBottom: aberta ? "none" : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={aberta ? `Recolher ${no.nome}` : `Expandir ${no.nome}`}>
            {aberta ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{no.nome}</TableCell>
        <TableCell align="right">{no.filhos.length}</TableCell>
        <TableCell align="right">{no.dias.length}</TableCell>
        <TableCell align="right">{no.acessos}</TableCell>
        <TableCell align="right">{no.erros || "—"}</TableCell>
        <TableCell>{formatarUltimo(no.ultimoAcesso)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, bgcolor: "action.hover" }}>
          <Collapse in={aberta} unmountOnExit>
            <Table size="small" sx={{ my: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{rotuloFilho}</TableCell>
                  <TableCell align="right">Acessos</TableCell>
                  <TableCell align="right">Erros</TableCell>
                  <TableCell>Dias de uso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {no.filhos.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>{f.nome}</TableCell>
                    <TableCell align="right">{f.acessos}</TableCell>
                    <TableCell align="right">{f.erros || "—"}</TableCell>
                    <TableCell>
                      <Dias dias={f.dias} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

function TabelaArvore({ nos, rotulo, rotuloFilhos, rotuloFilho }: { nos: No[]; rotulo: string; rotuloFilhos: string; rotuloFilho: string }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>{rotulo}</TableCell>
            <TableCell align="right">{rotuloFilhos}</TableCell>
            <TableCell align="right">Dias ativos</TableCell>
            <TableCell align="right">Acessos</TableCell>
            <TableCell align="right">Erros</TableCell>
            <TableCell>Último uso</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nos.map((no) => (
            <LinhaExpansivel key={no.id} no={no} rotuloFilho={rotuloFilho} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const UsoAppPanel = () => {
  const [clientes, setClientes] = useState<Client[]>([]);
  const [modulos, setModulos] = useState<ManifestModule[]>([]);
  const [cliente, setCliente] = useState<Client | null>(null);
  const [modulo, setModulo] = useState<ManifestModule | null>(null);
  const [de, setDe] = useState(() => dayjs().subtract(29, "day").format("YYYY-MM-DD"));
  const [ate, setAte] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [visao, setVisao] = useState<Visao>("usuario");
  const [linhas, setLinhas] = useState<UsageRow[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    getClients({ limit: 1000, offset: 0 })
      .then((r) => setClientes(r.clients ?? []))
      .catch(() => setClientes([]));
    getAllModules()
      .then((m) => setModulos(m ?? []))
      .catch(() => setModulos([]));
  }, []);

  useEffect(() => {
    if (!de || !ate) return;
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    getUsage({ client_id: cliente?.id, module_id: modulo?.id, de, ate })
      .then((r) => {
        if (!cancelado) setLinhas(r.linhas ?? []);
      })
      .catch((e: Error) => {
        if (!cancelado) {
          setLinhas([]);
          setErro(e.message);
        }
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [cliente, modulo, de, ate]);

  const nomeModulo = useMemo(() => {
    const rotulos = new Map(modulos.map((m) => [m.id, m.label]));
    return (id: string) => rotulos.get(id) ?? id;
  }, [modulos]);

  const r = useMemo(() => resumo(linhas), [linhas]);

  const niveis: Dimensao[] = visao === "usuario" ? ["usuario", "modulo"] : ["modulo", "usuario"];
  const nos = useMemo(() => arvore(linhas, niveis, nomeModulo), [linhas, visao, nomeModulo]);
  const porCliente = useMemo(() => arvore(linhas, ["cliente", "usuario"], nomeModulo), [linhas, nomeModulo]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <Autocomplete
          options={clientes}
          size="small"
          sx={{ minWidth: 240, flex: 1 }}
          value={cliente}
          onChange={(_, v) => setCliente(v)}
          getOptionLabel={(c) => c.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Cliente" placeholder="Todos os clientes" />}
        />
        <Autocomplete
          options={modulos}
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
          value={modulo}
          onChange={(_, v) => setModulo(v)}
          getOptionLabel={(m) => m.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Módulo" placeholder="Todos os módulos" />}
        />
        <TextField
          label="De"
          type="date"
          size="small"
          value={de}
          onChange={(e) => setDe(e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: ate } }}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          value={ate}
          onChange={(e) => setAte(e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: de } }}
        />
      </Stack>

      <Indicadores
        itens={[
          { titulo: "Usuários ativos", valor: r.usuarios, nota: cliente ? cliente.name : `em ${r.clientes} cliente(s)` },
          { titulo: "Módulos usados", valor: r.modulos, nota: "com ao menos um acesso" },
          { titulo: "Acessos", valor: r.acessos, nota: "aberturas de módulo" },
          { titulo: "Dias com uso", valor: r.dias, nota: `entre ${dayjs(de).format("DD/MM")} e ${dayjs(ate).format("DD/MM")}` },
        ]}
      />

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {carregando ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : linhas.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          Nenhum uso registrado no período. O indicador conta a partir da versão do app que envia o cliente nos logs.
        </Typography>
      ) : !cliente ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Usuários</TableCell>
                <TableCell align="right">Dias ativos</TableCell>
                <TableCell align="right">Acessos</TableCell>
                <TableCell>Último uso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {porCliente.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    setCliente(clientes.find((x) => x.id === c.id) ?? ({ id: c.id, name: c.nome } as Client))
                  }
                >
                  <TableCell sx={{ fontWeight: 600 }}>{c.nome}</TableCell>
                  <TableCell align="right">{c.filhos.length}</TableCell>
                  <TableCell align="right">{c.dias.length}</TableCell>
                  <TableCell align="right">{c.acessos}</TableCell>
                  <TableCell>{formatarUltimo(c.ultimoAcesso)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <Tabs value={visao} onChange={(_, v) => setVisao(v)} sx={{ mb: 2 }}>
            <Tab value="usuario" label="Por usuário" />
            <Tab value="modulo" label="Por módulo" />
          </Tabs>
          {visao === "usuario" ? (
            <TabelaArvore nos={nos} rotulo="Usuário" rotuloFilhos="Módulos" rotuloFilho="Módulo" />
          ) : (
            <TabelaArvore nos={nos} rotulo="Módulo" rotuloFilhos="Usuários" rotuloFilho="Usuário" />
          )}
        </>
      )}
    </>
  );
};
